import { PageHeader } from "@/components/data-display/page-header";
import { Badge } from "@/components/ui/badge";
import { InfoBanner } from "@/components/ui/banner";
import { Button } from "@/components/ui/button";
import { LoadingTable } from "@/components/ui/system-state";
import { Table, TableWrapper } from "@/components/ui/table";
import { BatchReleasePlanView } from "@/features/provider-migration/batch-release-plan-view";
import { ProviderMigrationNav } from "@/features/provider-migration/journey-nav";
import { CATEGORY_LABEL, UnavailableWithRetry } from "@/features/provider-migration/shared";
import { useProviderMigrationSummary } from "@/hooks/use-provider-migration";

/**
 * Provider migration, step 5: disposition status (page spec provider-migration-disposition-status).
 * No migration execution exists in this environment, so every discovered item is "Not started".
 * That is a derived label, stated as such, and the buckets always sum to the discovered total.
 */
export default function ProviderMigrationDispositionPage() {
  const s = useProviderMigrationSummary();
  const rows = [
    { key: "VAULT_ACCOUNTS", count: s.vaults.data?.items.length ?? 0 },
    { key: "ASSET_WALLETS", count: s.wallets.data?.items.length ?? 0 },
    { key: "VAULT_ADDRESSES", count: s.addresses.data?.items.length ?? 0 },
  ] as const;

  return (
    <div>
      <PageHeader
        title="Disposition status"
        subtitle="Where every discovered item stands. Nothing is left unaccounted for."
        actions={
          <>
            <Button variant="secondary" size="sm" onClick={s.refresh}>
              Refresh
            </Button>
          </>
        }
      />
      <ProviderMigrationNav active="disposition" />
      <InfoBanner variant="info" title="Read-only, no funds moved">
        This page only reads discovery and disposition status. It cannot move funds, change the provider workspace,
        or start a migration. A recorded batch release plan is evidence only and never authorizes execution.
      </InfoBanner>
      <BatchReleasePlanView state={{ status: "unavailable", reasonCode: "BATCH_RELEASE_PLAN_UNAVAILABLE" }} />

      {s.loading && <LoadingTable rows={3} cols={3} />}
      {!s.loading && s.error && (
        <UnavailableWithRetry title="Disposition status is not available" error={s.error} onRetry={s.refresh} />
      )}
      {!s.loading && !s.error && (
        <>
          <TableWrapper>
            <Table>
              <thead>
                <tr>
                  <th>Resource type</th>
                  <th>Discovered</th>
                  <th>Migrated</th>
                  <th>Retained at source</th>
                  <th>Not started</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.key}>
                    <td>{CATEGORY_LABEL[r.key]}</td>
                    <td>{r.count}</td>
                    <td>0</td>
                    <td>0</td>
                    <td>
                      <Badge variant="neutral">{r.count}</Badge>
                    </td>
                  </tr>
                ))}
                <tr>
                  <td className="font-semibold">Total</td>
                  <td className="font-semibold">{s.total}</td>
                  <td>0</td>
                  <td>0</td>
                  <td className="font-semibold">{s.total}</td>
                </tr>
              </tbody>
            </Table>
          </TableWrapper>
          <p className="mt-3 max-w-2xl text-xs text-muted">
            Derived from the discovered inventory: migration execution is not available in this environment, so every item is
            shown as Not started and the buckets add up to the discovered total.
            {s.truncated && " The inventory read hit its cap, so totals are a lower bound."}
          </p>
        </>
      )}
    </div>
  );
}
